import {
  contactMatches,
  homeTab as homeTabFor,
  isSalesRep,
  ownTab,
  repTabs,
  jobAddress,
  jobContractValues,
  jobMatches,
  navItems,
  partnersEnabled,
  formatAddress,
  NAV_ITEMS,
  type Cents,
  type Contact,
  type Job,
  type NavItem,
} from "@roof-hawk/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  useAsync,
  useHashRoute,
  useRefreshOnReturn,
  navigate,
  rememberValue,
  rememberedValue,
} from "../../lib/hooks";
import {
  fetchCompanySettings,
  fetchContacts,
  fetchEstimateApprovals,
  fetchJobContractParts,
  fetchJobs,
} from "../../lib/queries";
import { fetchCloseouts, type CloseoutRow } from "../../lib/closeout";
import { readJobRoute } from "../../lib/jobLink";

import { LOGO_DATA_URI } from "../../logo";
import { TaskList } from "./TaskList";
import { useSession } from "../../session";
import { ContactDetail } from "../contacts/ContactDetail";
import { ContactsList } from "../contacts/ContactsList";
import { NewLeadModal } from "../contacts/NewLeadModal";
import { JobDetail } from "../job/JobDetail";
import { MarketingScreen } from "../marketing/MarketingScreen";
import { AiTeamScreen } from "../aiteam/AiTeamScreen";
import { PhoneBubble } from "../phone/PhoneBubble";
import { Partners, isPartnerRoute } from "../partners/Partners";
import { Calendar } from "../lists/Calendar";
import { JobsList } from "../lists/JobsList";
import { MasterList } from "../lists/MasterList";
import { Pipeline } from "../lists/Pipeline";
import { PayoutsScreen } from "../lists/PayoutsScreen";
import { ProductionBoard } from "../lists/ProductionBoard";
import { RepJobList } from "../lists/RepJobList";
import { CloseoutBoard } from "../lists/CloseoutBoard";
import { ReportsScreen } from "../reports/ReportsScreen";
import { Tools } from "../tools/Tools";
import { isQuickbooksReturn } from "../tools/IntegrationsTool";
import { t, tn } from "../../i18n";
import { LanguageButton } from "./LanguageButton";
import { ScentButton } from "./ScentButton";
import { ThemeButton } from "./ThemeButton";
import { VersionFooter } from "./VersionFooter";

/** Everything the list screens read, loaded once and refreshed after writes. */
export interface AppData {
  jobs: Job[];
  contacts: Contact[];
  /**
   * Contract dollars per job id, or null until they have arrived.
   *
   * Loaded once here rather than per screen: filtering a list or typing in
   * search then costs nothing, where fetching per visible row would re-query on
   * every keystroke. Null rather than an empty map on purpose — a list can then
   * leave the column blank instead of printing $0.00 and changing its mind.
   */
  values: ReadonlyMap<string, Cents> | null;
  /**
   * When each job's estimate was approved, for the jobs where one was.
   *
   * Only ever stands in for a job with no signed date — `soldDate` in
   * packages/core has the order. Empty until it has arrived, which reads the
   * same as "no approvals" and is harmless: the signed date is the answer for
   * almost every job anyway.
   */
  approvals: ReadonlyMap<string, string>;
  /**
   * The jobs sent to be closed, by job id (ADR 0273). Empty until it has
   * arrived, and empty on a database without the table — read on its own so
   * that can never take the lists down. Absent reads as none sent.
   */
  closeouts?: ReadonlyMap<string, CloseoutRow>;
  reload: () => Promise<void>;
}

/**
 * Which screen the app opens on, given the path it was loaded with.
 *
 * Both halves matter, and the first version of the QuickBooks callback got
 * that wrong: it set `view` to Tools and left `repTab` on the rep's own id.
 * Further down, a rep's pipeline is chosen *before* `view === "Tools"` is ever
 * reached, so the app opened on somebody's leads with Intuit's single-use code
 * still sitting unread in the query string — the connection silently failed and
 * pressing Connect again started over. It was found by following the real
 * redirect on production, not by reading the code, which is why the rule now
 * lives in a function with a test rather than in two `useState` calls.
 */
export function landingScreen(
  pathname: string,
  homeTab: string,
  /**
   * `"<view>|<repTab>"` — the screen this browser tab was last on, when it was
   * remembered. See `WHERE` below and `rememberedValue` in lib/hooks.
   */
  remembered?: string | null,
  /**
   * The tabs this person has — fewer for a field login (ADR 0322). A
   * remembered tab they no longer have is forgotten rather than reopened.
   */
  tabs: readonly NavItem[] = NAV_ITEMS,
): { view: NavItem; repTab: string } {
  if (isQuickbooksReturn(pathname)) return { view: "Tools", repTab: "" };

  const [view, repTab] = (remembered ?? "").split("|");
  /* Partners is left out on purpose rather than forgotten: the section keeps
     its own hashes, so the address bar already brings a refresh back to it,
     to the very partner that was open, which the tab name alone cannot. */
  if (view && view !== "Partners" && (tabs as readonly string[]).includes(view))
    return { view: view as NavItem, repTab: repTab ?? "" };

  return { view: "Prospects", repTab: homeTab };
}

/** The tab that opens the close-out board (ADR 0273). */
const CLOSEOUT_TAB = "closeout";

/** The key the shell's own "where was I" is kept under. */
const WHERE = "shell";

export function Shell() {
  const { people, displayName, signOut, profile } = useSession();
  /**
   * Home is your own pipeline: it is the screen a rep works from all day, so
   * signing in and clicking the logo both land there rather than on a list of
   * everybody's jobs. A field person's home is their own tab too, the jobs
   * they are on (ADR 0322). Anyone else — no linked profile, or neither role
   * — keeps the old landing, which is the shared Prospects list.
   */
  const homeTab = homeTabFor(profile);
  /** The tabs across the top: fewer for a field login (ADR 0322). */
  const tabs = navItems(profile);
  /**
   * Intuit sends the browser back to `/tools/quickbooks?code=…` after somebody
   * says yes to QuickBooks, and the code is single-use. Landing on Prospects
   * would drop it on the floor and the connection would silently never happen,
   * so that one path opens Tools instead (ADR 0038).
   */
  const landing = landingScreen(
    window.location.pathname,
    homeTab,
    rememberedValue(WHERE),
    tabs,
  );
  const [view, setView] = useState<NavItem>(landing.view);
  const [repTab, setRepTab] = useState(landing.repTab);
  const [search, setSearch] = useState("");
  const [showSearchDrop, setShowSearchDrop] = useState(false);
  /* The drop closes a beat after the box loses focus, so that the click which
     took the focus lands on its row first. The beat is cancelled when the shell
     goes away: a timer outliving its component calls into a page that is no
     longer there. */
  const searchBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (searchBlurTimer.current) clearTimeout(searchBlurTimer.current);
    },
    [],
  );
  const [newLeadFor, setNewLeadFor] = useState<Contact | null | undefined>(
    undefined,
  );
  const hash = useHashRoute();

  const jobsQuery = useAsync(fetchJobs, []);
  const contactsQuery = useAsync(fetchContacts, []);
  const partsQuery = useAsync(fetchJobContractParts, []);
  const approvalsQuery = useAsync(fetchEstimateApprovals, []);
  const closeoutsQuery = useAsync(fetchCloseouts, []);
  /*
   * Only for `partners_enabled`. The Partners tab is always drawn; this
   * decides whether it opens onto the whole partner network or onto the
   * business card file alone (ADR 0262, amending ADR 0144).
   */
  const companyQuery = useAsync(fetchCompanySettings, []);
  const partnersOn = partnersEnabled(companyQuery.data?.partners_enabled);
  /** Who finished jobs go to, for her tab and her board (ADR 0273). */
  const closeoutProfileId = companyQuery.data?.closeout_profile_id ?? null;
  const closeoutOwner =
    people.find((p) => p.id === closeoutProfileId && p.active !== false) ??
    null;

  const reload = useCallback(async () => {
    await Promise.all([
      jobsQuery.reload(),
      contactsQuery.reload(),
      partsQuery.reload(),
      approvalsQuery.reload(),
      closeoutsQuery.reload(),
    ]);
  }, [jobsQuery, contactsQuery, partsQuery, approvalsQuery, closeoutsQuery]);

  // Somebody else's lead should not need a page refresh to be visible.
  useRefreshOnReturn(reload);

  const data: AppData = useMemo(
    () => ({
      jobs: jobsQuery.data ?? [],
      contacts: contactsQuery.data ?? [],
      values:
        jobsQuery.data && partsQuery.data
          ? jobContractValues(jobsQuery.data, partsQuery.data)
          : null,
      approvals: approvalsQuery.data ?? new Map(),
      closeouts: closeoutsQuery.data ?? new Map(),
      reload,
    }),
    [
      jobsQuery.data,
      contactsQuery.data,
      partsQuery.data,
      approvalsQuery.data,
      closeoutsQuery.data,
      reload,
    ],
  );

  const onDetail =
    hash.startsWith("job=") ||
    hash.startsWith("contact=") ||
    isPartnerRoute(hash);

  // Close the typeahead when the route changes.
  useEffect(() => setShowSearchDrop(false), [hash]);

  const searchHits = useMemo(() => {
    const query = search.trim();
    if (!query) return null;
    return {
      contacts: data.contacts
        .filter((c) => contactMatches(c, query))
        .slice(0, 4),
      jobs: data.jobs.filter((j) => jobMatches(j, query)).slice(0, 6),
    };
  }, [search, data.contacts, data.jobs]);

  const jobCountFor = useCallback(
    (contactId: string) =>
      data.jobs.filter((j) => j.contact_id === contactId).length,
    [data.jobs],
  );

  /**
   * Move to a list, and remember having done it.
   *
   * The pair moves together — a rep's tab belongs to the list behind it — so
   * it is written as one string rather than two keys that could be restored
   * half and half.
   */
  function go(nextView: NavItem, nextRepTab: string) {
    setView(nextView);
    setRepTab(nextRepTab);
    rememberValue(WHERE, `${nextView}|${nextRepTab}`);
  }

  function goList(nextView: NavItem) {
    go(nextView, "");
    // Partners owns its own hashes, so the tab lands on a named route rather
    // than on the empty one — otherwise the section's own strip has nothing to
    // mark as current, and Back from a partner has nowhere to go.
    navigate(nextView === "Partners" ? "partners" : "");
  }

  function goHome() {
    go("Prospects", homeTab);
    navigate("");
  }

  const error = jobsQuery.error ?? contactsQuery.error;

  return (
    <div id="appView">
      <div className="topbar">
        <div className="brand" onClick={goHome} role="button" tabIndex={0}>
          <img src={LOGO_DATA_URI} alt="Roof Board" />
        </div>

        <button className="addlead" onClick={() => setNewLeadFor(null)}>
          {t("+ New Lead")}
        </button>

        <nav className="top">
          {tabs.map((item) => (
            <div
              key={item}
              className={`item${
                item === view && repTab === "" && !onDetail ? " active" : ""
              }`}
              onClick={() => goList(item)}
            >
              {t(item)}
            </div>
          ))}
        </nav>

        <div className="spacer" />

        <div className="search">
          <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4.3-4.3" />
          </svg>
          <input
            placeholder={t("Search name, address, phone, job #…")}
            autoComplete="off"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowSearchDrop(true);
            }}
            onBlur={() => {
              if (searchBlurTimer.current)
                clearTimeout(searchBlurTimer.current);
              searchBlurTimer.current = setTimeout(() => {
                searchBlurTimer.current = null;
                setShowSearchDrop(false);
              }, 200);
            }}
          />
          {showSearchDrop &&
            searchHits &&
            (searchHits.contacts.length > 0 || searchHits.jobs.length > 0) && (
              <div className="ac-drop">
                {searchHits.contacts.length > 0 && (
                  <>
                    <div className="ac-sec">{t("Customers")}</div>
                    {searchHits.contacts.map((contact) => (
                      <div
                        key={contact.id}
                        className="ac-item"
                        onMouseDown={() => {
                          setSearch("");
                          navigate(`contact=${contact.id}`);
                        }}
                      >
                        <div className="an">{contact.full_name ?? "—"}</div>
                        <div className="aa">
                          {formatAddress(contact)} ·{" "}
                          {tn(jobCountFor(contact.id), "1 job", "{n} jobs")}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                {searchHits.jobs.length > 0 && (
                  <>
                    <div className="ac-sec">{t("Jobs")}</div>
                    {searchHits.jobs.map((job) => (
                      <div
                        key={job.id}
                        className="ac-item"
                        onMouseDown={() => {
                          setSearch("");
                          navigate(`job=${job.id}`);
                        }}
                      >
                        <div className="an">
                          {job.contact?.full_name ?? "—"}
                        </div>
                        <div className="aa">
                          {jobAddress(job)} · #{job.job_number}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
        </div>

        <TaskList jobs={data.jobs} />

        <ScentButton />

        <ThemeButton />

        <LanguageButton />

        <span
          className="signout"
          onClick={() => void signOut()}
          title={displayName}
        >
          {t("Sign out")}
        </span>
      </div>

      <div className="reptabs">
        <div
          className={`reptab${repTab === "" && !onDetail ? " active" : ""}`}
          onClick={() => {
            go(view, "");
            navigate("");
          }}
        >
          {t("All")}
        </div>
        {/* A field person's own tab, the jobs they are on, drawn first
            because it is their home screen; a rep already has theirs below
            (ADR 0322). */}
        {ownTab(profile) && (
          <div
            className={`reptab${
              repTab === profile?.id && !onDetail ? " active" : ""
            }`}
            onClick={() => {
              go(view, homeTab);
              navigate("");
            }}
          >
            {profile?.full_name}
          </div>
        )}
        {/* Only people who are still here, and only people who sell.

            A deactivated profile was being drawn regardless, so retiring
            somebody never took them off the top of the screen — which is half
            of why Lysa and Sam looked duplicated. Filtered here rather than in
            the query that loads them: jobs go on pointing at whoever sold them,
            and every screen that names a person still has to be able to find
            them.

            The role check came later, 2026-09-06: Jonathon asked why Creed and
            Carlos were up there. Every active person was, whatever they do — a
            bookkeeper, a project manager, an inspector, an agent — and a tab
            for somebody who cannot hold a job opens an empty pipeline. Carlos
            is an active project manager and inspector, so deactivating him to
            clear the tab would have cost him the rest of the app. Reading the
            role instead means giving somebody `sales_rep` is what puts them
            back, which is the answer to "at this time". */}
        {repTabs(people).map((person) => (
          <div
            key={person.id}
            className={`reptab${
              repTab === person.id && !onDetail ? " active" : ""
            }`}
            onClick={() => {
              go(view, person.id);
              navigate("");
            }}
          >
            {person.full_name}
          </div>
        ))}
        {/* The person finished jobs go to — Kelsey. She sells nothing, so
            the rule above leaves her off; this tab is her board of every
            sold job instead (ADR 0273). It has its own key rather than her
            id, so that a rep chosen instead keeps their pipeline on their
            own tab and gets the board as a second one, named apart. */}
        {closeoutOwner && (
          <div
            className={`reptab${
              repTab === CLOSEOUT_TAB && !onDetail ? " active" : ""
            }`}
            onClick={() => {
              go(view, CLOSEOUT_TAB);
              navigate("");
            }}
          >
            {isSalesRep(closeoutOwner)
              ? t("{name} · Closing", { name: closeoutOwner.full_name ?? "" })
              : closeoutOwner.full_name}
          </div>
        )}
        <div
          className={`reptab master${
            repTab === "master" && !onDetail ? " active" : ""
          }`}
          onClick={() => {
            go(view, "master");
            navigate("");
          }}
        >
          {t("Master (Sold)")}
        </div>
      </div>

      <div className="wrap">
        {error ? (
          <div className="list">
            <div className="empty">
              <b>{t("Could not load")}</b>
              {error}
            </div>
          </div>
        ) : (
          <Content
            hash={hash}
            view={view}
            repTab={repTab}
            search={search}
            data={data}
            partnersOn={partnersOn}
            closeoutProfileId={closeoutOwner?.id ?? null}
            onNewJobFor={(contact) => setNewLeadFor(contact)}
          />
        )}
      </div>

      <VersionFooter />

      {/* The phone, in the corner of every screen (ADR 0321). */}
      <PhoneBubble data={data} />

      {newLeadFor !== undefined && (
        <NewLeadModal
          contact={newLeadFor}
          contacts={data.contacts}
          jobs={data.jobs}
          jobCountFor={jobCountFor}
          onClose={() => setNewLeadFor(undefined)}
          onCreated={async (jobId) => {
            // Navigate first, reload after. The reload takes a second against
            // the live database, and going second meant the modal closed onto
            // whatever list was behind it — the rep's own pipeline — for long
            // enough to read as "it didn't work, and it's put me somewhere
            // else". The lists behind catch up a moment later.
            navigate(`job=${jobId}`);
            setNewLeadFor(undefined);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function Content({
  hash,
  view,
  repTab,
  search,
  data,
  partnersOn,
  closeoutProfileId,
  onNewJobFor,
}: {
  hash: string;
  view: NavItem;
  repTab: string;
  search: string;
  data: AppData;
  /**
   * `company_settings.partners_enabled` — off leaves the section showing the
   * business card file and nothing else (ADR 0262).
   */
  partnersOn: boolean;
  /** Whose tab opens the close-out board rather than a pipeline. */
  closeoutProfileId: string | null;
  onNewJobFor: (contact: Contact) => void;
}) {
  const { people } = useSession();

  /* Keyed by the record, on both of these, so that opening a different one
     builds a different screen.

     Every detail screen here holds on to the last record it loaded rather
     than blanking while it fetches, which is right for a refresh of the
     record you are looking at and wrong for a move to another one: React
     sees the same component in the same place, keeps it, and hands it a new
     id, so the customer you were on stays fully drawn until the new one
     arrives — and stays for good if the fetch fails. Jonathon, 2026-09-18,
     searching a second customer from inside the first: "it just keeps us on
     the original customer's page." The id is the screen's identity, so it is
     its key. */
  if (hash.startsWith("job=")) {
    const { jobId, card } = readJobRoute(hash);
    return <JobDetail key={jobId} jobId={jobId} data={data} card={card} />;
  }
  /* A link straight into Tools — the to-do list sending somebody to the
     supplier mail that needs placing (ADR 0311). Which section opens is the
     remembered one, which the link sets before it navigates. */
  if (hash === "tools") return <Tools />;
  /* The partner section owns its own hashes, so a link from anywhere — a
     storm batch, a referral, the rep's send queue — lands on the right screen
     without the top nav having to know which one it was.

     While the network is off the section itself decides that a storm batch
     or a send queue is not a screen, and shows the card file instead
     (ADR 0262). */
  if (isPartnerRoute(hash))
    return <Partners search={search} network={partnersOn} />;
  if (hash.startsWith("contact=")) {
    const contactId = hash.slice(8);
    return (
      <ContactDetail
        key={contactId}
        contactId={contactId}
        data={data}
        onNewJob={onNewJobFor}
      />
    );
  }
  // Where a missed-call push lands when the caller has no job to open. The
  // screen was reachable only by tapping Reports, so the notification about a
  // stranger on the doorstep used to open the pipeline instead — the one case
  // this screen exists for.
  if (hash.startsWith("reports=")) {
    return (
      <ReportsScreen jobs={data.jobs} values={data.values} section="calls" />
    );
  }

  if (repTab === "master") return <MasterList data={data} search={search} />;

  const closeoutOwner = people.find((p) => p.id === closeoutProfileId);
  if (repTab === CLOSEOUT_TAB && closeoutOwner)
    return <CloseoutBoard owner={closeoutOwner} data={data} search={search} />;

  const person = people.find((p) => p.id === repTab);
  // Her own id, as a browser tab remembered from before the board had a key
  // of its own. A rep's own id is always their pipeline.
  if (person && person.id === closeoutProfileId && !isSalesRep(person))
    return <CloseoutBoard owner={person} data={data} search={search} />;
  if (person) {
    const isRep =
      Array.isArray(person.roles) && person.roles.includes("sales_rep");
    return isRep ? (
      <Pipeline person={person} data={data} search={search} />
    ) : (
      <RepJobList person={person} data={data} search={search} />
    );
  }

  if (view === "Ongoing")
    return <ProductionBoard data={data} search={search} />;
  // The insurance the paid crews cost sits under the board that pays them
  // (ADR 0275).
  if (view === "Payouts") return <PayoutsScreen />;
  if (view === "Cal") return <Calendar data={data} />;
  if (view === "Contacts") return <ContactsList data={data} search={search} />;
  if (view === "Tools") return <Tools />;
  // Everyone sees all of it and may dismiss a finding (ADR 0259).
  if (view === "AI Team") return <AiTeamScreen data={data} />;
  if (view === "Partners")
    return <Partners search={search} network={partnersOn} />;
  if (view === "Reports")
    return <ReportsScreen jobs={data.jobs} values={data.values} />;
  if (view === "Marketing") return <MarketingScreen jobs={data.jobs} />;

  return <JobsList view={view} data={data} search={search} />;
}
